import React, { useRef, useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'
import { uploadToGetInsertedImageStaticUrl } from '../../api/file'
import { message } from 'antd'
import { loadUser } from '../../utils/storage'
import {
  DeleteOutlined,
  LinkOutlined,
  PictureOutlined,
  ClearOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  AlignRightOutlined as AlignJustifyOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  ColumnWidthOutlined,
  PicCenterOutlined
} from '@ant-design/icons'

const EditorContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;

  .editor-toolbar {
    padding: 8px 12px;
    background: #fafafa;
    border-bottom: 1px solid #d9d9d9;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    align-items: center;

    button,
    select {
      padding: 6px 12px;
      border: 1px solid #d9d9d9;
      background: white;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;

      &:hover {
        background: #f0f0f0;
      }

      &.active {
        background: #1890ff;
        color: white;
        border-color: #1890ff;
      }

      &.danger {
        color: #ff4d4f;
        border-color: #ff4d4f;

        &:hover {
          background: #fff1f0;
        }
      }
    }

    select {
      min-width: 100px;
    }

    .separator {
      width: 1px;
      height: 24px;
      background: #d9d9d9;
      margin: 0 4px;
    }
  }

  .editor-content {
    min-height: 500px;
    max-height: 1000px;
    overflow-y: auto;
    padding: 16px;
    font-family: "Gilroy", sans-serif;
    font-size: 16px;
    line-height: 1.6;
    outline: none;

    &:focus {
      outline: none;
    }

    /* Image selection styling */
    img {
      cursor: pointer;
      transition: outline 0.2s ease;

      &:hover {
        outline: 2px dashed #1890ff;
        outline-offset: 2px;
      }

      &.selected {
        outline: 3px solid #1890ff;
        outline-offset: 2px;
        box-shadow: 0 0 8px rgba(24, 144, 255, 0.4);
      }
    }
  }
`

// Font family options for the dropdown
const commonFonts = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Arial Black', label: 'Arial Black' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Garamond', label: 'Garamond' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Courier', label: 'Courier' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: 'Trebuchet MS', label: 'Trebuchet MS' },
  { value: 'Impact', label: 'Impact' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS' },
  { value: 'Lucida Console', label: 'Lucida Console' },
  { value: 'Palatino Linotype', label: 'Palatino Linotype' },
  { value: 'Book Antiqua', label: 'Book Antiqua' },
  { value: 'Century Gothic', label: 'Century Gothic' },
  { value: 'Franklin Gothic Medium', label: 'Franklin Gothic Medium' },
  { value: 'Bookman', label: 'Bookman' },
  { value: 'Avant Garde', label: 'Avant Garde' }
]

const genericFonts = [
  { value: 'serif', label: 'Serif' },
  { value: 'sans-serif', label: 'Sans-serif' },
  { value: 'monospace', label: 'Monospace' },
  { value: 'cursive', label: 'Cursive' },
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'system-ui', label: 'System UI' }
]

// Font size options (2 to 48px dynamically generated)
const fontSizes = Array.from({ length: 47 }, (_, i) => {
  const size = i + 2
  return { value: size, label: `${size}px` }
})

const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Edit your content here...'
}) => {
  const editorRef = useRef(null)
  const fileInputRef = useRef(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && !isInitialized && value) {
      editorRef.current.innerHTML = value
      setIsInitialized(true)
    }
  }, [value, isInitialized])

  // Update content when value changes significantly
  useEffect(() => {
    if (editorRef.current && isInitialized) {
      const currentContent = editorRef.current.innerHTML
      if (value && currentContent !== value) {
        // Only update if the value is significantly different
        editorRef.current.innerHTML = value
      }
    }
  }, [value, isInitialized])

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value)
    editorRef.current && editorRef.current.focus()
    handleInput()
  }, [])

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML)
    }
  }, [onChange])

  // Get selected image in the editor
  const getSelectedImage = useCallback(() => {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    let node = range.commonAncestorContainer

    // If the node is text, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode
    }

    // Check if the node or its parent is an image
    if (node.tagName === 'IMG') {
      return node
    }

    // Check if any child is an image
    const img = node.querySelector && node.querySelector('img')
    if (img) {
      return img
    }

    return null
  }, [])

  // Handle image selection
  const handleImageClick = useCallback(
    (e) => {
      if (e.target.tagName === 'IMG') {
        // Remove selection from previous image
        if (selectedImage) {
          selectedImage.classList.remove('selected')
        }

        // Select new image
        e.target.classList.add('selected')
        setSelectedImage(e.target)
      } else {
        // Remove selection if clicking elsewhere
        if (selectedImage) {
          selectedImage.classList.remove('selected')
          setSelectedImage(null)
        }
      }
    },
    [selectedImage],
  )

  // Delete selected image
  const deleteSelectedImage = useCallback(() => {
    if (selectedImage) {
      const img = selectedImage
      const parent = img.parentNode

      // Create a text node to maintain cursor position
      const textNode = document.createTextNode('\u200B') // Zero-width space
      parent.insertBefore(textNode, img)
      parent.removeChild(img)

      // Place cursor at the position of the deleted image
      const range = document.createRange()
      range.setStartAfter(textNode)
      range.setEndAfter(textNode)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)

      // Clear selection state
      setSelectedImage(null)
      handleInput()
    }
  }, [selectedImage, handleInput])

  // Handle image file upload and insert
  const handleImageUpload = useCallback(() => {
    if (selectedImage) {
      message.warning(
        'Please deselect the current image first, or delete it before inserting a new one.',
      )
      return
    }
    if (isUploading) {
      return
    }
    fileInputRef.current && fileInputRef.current.click()
  }, [selectedImage, isUploading])

  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files && e.target.files[0]
      if (!file) return

      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }

      // Focus the editor first to get the cursor position
      if (editorRef.current) {
        editorRef.current.focus()
      }

      // Get the logged-in user's ID
      const user = loadUser()
      const personId = user && user.id

      // Show uploading message
      setIsUploading(true)
      const loadingMessage = message.loading(
        'Inserting image, please wait...',
        0,
      )

      try {
        // Upload the file to get a proper URL
        const response = await uploadToGetInsertedImageStaticUrl({
          file: file,
          personId: personId
        })

        if (response && response.url) {
          const imageUrl = response.url

          // Insert image at cursor position
          const selection = window.getSelection()
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            range.deleteContents()

            const img = document.createElement('img')
            img.src = imageUrl
            img.style.maxWidth = '100%'
            img.style.height = 'auto'
            range.insertNode(img)

            // Move cursor after the image
            range.setStartAfter(img)
            range.setEndAfter(img)
            selection.removeAllRanges()
            selection.addRange(range)

            handleInput()
          }
          loadingMessage()
          message.success('Image inserted successfully!')
        }
      } catch (error) {
        console.error('Image insertion failed:', error)
        loadingMessage()
        message.error('Failed to insert image. Please try again.')
      } finally {
        setIsUploading(false)
      }

      // Reset the input so the same file can be selected again
      if (e.target) {
        e.target.value = ''
      }
    },
    [handleInput, isUploading],
  )

  const handleKeyDown = useCallback(
    (e) => {
      // Handle tab key for indentation
      if (e.key === 'Tab') {
        e.preventDefault()
        execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;')
      }

      // Handle Backspace/Delete for selected images
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedImage) {
        e.preventDefault()
        deleteSelectedImage()
      }
    },
    [execCommand, selectedImage, deleteSelectedImage],
  )

  // Toolbar button component
  const ToolbarButton = ({
    command,
    label,
    title,
    value: cmdValue,
    onClick,
    className
  }) => (
    <button
      type="button"
      title={title || label}
      className={className}
      onMouseDown={(e) => {
        e.preventDefault()
        if (onClick) {
          onClick()
        } else {
          execCommand(command, cmdValue)
        }
      }}
    >
      {label}
    </button>
  )

  return (
    <EditorContainer>
      <div className="editor-toolbar">
        {/* Text Formatting */}
        <ToolbarButton command="bold" label={<BoldOutlined />} title="Bold" />
        <ToolbarButton
          command="italic"
          label={<ItalicOutlined />}
          title="Italic"
        />
        <ToolbarButton
          command="underline"
          label={<UnderlineOutlined />}
          title="Underline"
        />
        <ToolbarButton
          command="strikeThrough"
          label={<StrikethroughOutlined />}
          title="Strikethrough"
        />

        <div className="separator" />

        {/* Font Family */}
        <select
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) {
              execCommand('fontName', e.target.value)
              e.target.value = ''
            }
          }}
        >
          <option value="">Font</option>
          <optgroup label="Common Fonts">
            {commonFonts.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </optgroup>
          <optgroup label="Generic Families">
            {genericFonts.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </optgroup>
        </select>

        {/* Font Size */}
        <select
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) {
              execCommand('fontSize', e.target.value)
            }
          }}
        >
          <option value="">Size</option>
          {fontSizes.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>

        <div className="separator" />

        {/* Alignment */}
        <ToolbarButton
          command="justifyLeft"
          label={<AlignLeftOutlined />}
          title="Align Left"
        />
        <ToolbarButton
          command="justifyCenter"
          label={<AlignCenterOutlined />}
          title="Align Center"
        />
        <ToolbarButton
          command="justifyRight"
          label={<AlignRightOutlined />}
          title="Align Right"
        />
        <ToolbarButton
          command="justifyFull"
          label={<AlignJustifyOutlined />}
          title="Justify"
        />

        <div className="separator" />

        {/* Lists */}
        <ToolbarButton
          command="insertOrderedList"
          label={<OrderedListOutlined />}
          title="Numbered List"
        />
        <ToolbarButton
          command="insertUnorderedList"
          label={<UnorderedListOutlined />}
          title="Bullet List"
        />

        <div className="separator" />

        {/* Indentation */}
        <ToolbarButton
          command="outdent"
          label={<ColumnWidthOutlined />}
          title="Decrease Indent"
        />
        <ToolbarButton
          command="indent"
          label={<PicCenterOutlined />}
          title="Increase Indent"
        />

        <div className="separator" />

        {/* Colors */}
        <input
          type="color"
          title="Text Color"
          onChange={(e) => execCommand('foreColor', e.target.value)}
          style={{
            width: 30,
            height: 30,
            padding: 0,
            border: 'none',
            cursor: 'pointer'
          }}
        />
        <input
          type="color"
          title="Background Color"
          onChange={(e) => execCommand('hiliteColor', e.target.value)}
          style={{
            width: 30,
            height: 30,
            padding: 0,
            border: 'none',
            cursor: 'pointer'
          }}
        />

        <div className="separator" />

        {/* Links and Images */}
        <ToolbarButton
          command="createLink"
          label={<LinkOutlined />}
          title="Insert Link"
        />
        <ToolbarButton
          label={isUploading ? '⏳' : <PictureOutlined />}
          title={
            isUploading
              ? 'Inserting...'
              : selectedImage
              ? 'Deselect image first'
              : 'Insert Image'
          }
          onClick={handleImageUpload}
          className={isUploading ? '' : selectedImage ? 'danger' : ''}
          disabled={isUploading}
        />
        <ToolbarButton
          label={<DeleteOutlined />}
          title="Remove Selected Image"
          className={selectedImage ? 'danger' : ''}
          onClick={deleteSelectedImage}
        />

        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className="separator" />

        {/* Clear Formatting */}
        <ToolbarButton
          command="removeFormat"
          label={<ClearOutlined />}
          title="Clear Formatting"
        />
      </div>

      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleImageClick}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </EditorContainer>
  )
}

export default RichTextEditor
